import { Drawer, Loading, useExpandedRowKeysContext } from '@/components';
import { useOrganizationRecursive } from '@/features/tenant-manager/organization/_hooks/organization';
import { useMessage } from '@/providers';
import { findUpperRecursive, isRS } from '@/utils';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { MasterTableEditWarning } from '../../components/MasterTableEditWarning';
import {
  useCreatePolicy,
  usePolicies,
  usePolicy,
  usePolicyRecursive,
  useUpdatePolicy,
} from '../_hooks/policy';
import type { PolicyFormSchema } from '../_models/policy';
import { useDrawerState } from './DrawerStateProvider';
import { PolicyForm } from './PolicyForm';

export const PolicyFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();

  const isOpen = drawerState.open && drawerState.type === 'policy';
  const isEditing = isOpen && drawerState.isEditing;

  const policy = usePolicy(isEditing && drawerState.id ? drawerState.id : '');
  const policyRecursive = usePolicyRecursive(policy.data?.upperId ?? '');
  const policies = usePolicies();

  const organizationId = policy.data?.organizationId ?? '';

  const organizationIds = useOrganizationRecursive({
    id: organizationId,
    select: (data) => data.map((d) => d.id),
    enabled: !!organizationId,
  });

  const { addExpandedRowKeys } = useExpandedRowKeysContext();
  const { mutateAsync: createMutateAsync } = useCreatePolicy();
  const { mutateAsync: updateMutateAsync } = useUpdatePolicy();

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} destroyOnClose>
      {isRS() && isEditing && <MasterTableEditWarning marginBottom={8} />}
      {isEditing &&
      (policy.isPending || (organizationId && organizationIds.isPending)) ? (
        <Loading />
      ) : (
        <PolicyForm
          type={isEditing ? 'edit' : 'new'}
          initialValues={
            isEditing && policy.data
              ? ((): z.infer<typeof PolicyFormSchema> => {
                  const { startYear, endYear, ...initialValues } = policy.data;
                  return {
                    ...initialValues,
                    upperIds: (policyRecursive.data ?? []).map((d) => d.id),
                    organizationIds: organizationIds.data,
                    period: [
                      startYear ? dayjs().year(startYear) : undefined,
                      endYear ? dayjs().year(endYear) : undefined,
                    ],
                  };
                })()
              : undefined
          }
          onSubmit={async (values) => {
            invariant(isOpen, t('unreachable-code'));

            const res = isEditing
              ? await updateMutateAsync({ ...values, id: drawerState.id })
              : await createMutateAsync({ ...values });

            await Promise.all([
              openMessage(
                'success',
                isEditing
                  ? t('message-update-completed')
                  : t('message-create-completed'),
              ),
              handleClose(),
              !isEditing &&
                (async () => {
                  if (res.upperId) {
                    const uppers = findUpperRecursive(
                      res.upperId,
                      policies.data ?? [],
                    );

                    addExpandedRowKeys([res.id, ...uppers.map((d) => d.id)]);
                  }
                })(),
            ]);
          }}
        />
      )}
    </Drawer>
  );
};
